import { type BookmakersConfig } from '@cx-web/api-clients/site-config-api/types';
import { useToggle } from '@fpsm/cxy-components/Accordion';
import { GridTable } from '@fpsm/cxy-components/GridTable';

import { type BettingInsightsContent, type EsIndex } from '~/configuration/types';
import { type BettingInsightsUI, type StyleSheet } from '~/configuration/ui/BettingInsightsUI';

export type SectionProps = {
  'aria-labelledby': string;
  className: string;
  hidden: boolean;
  id: string;
  role: string;
};

export type ResultTableProps<TIndex extends EsIndex, TContent = BettingInsightsContent<TIndex>> = {
  bookmakers: BookmakersConfig;
  content: TContent;
  label: string;
  resourcesUrl: string;
  resultIndex: number;
  styles: StyleSheet;
  // @ts-ignore
  table: BettingInsightsUI<TIndex>['card']['table'];
  tableIndex: number;
};

export function ResultTable<TIndex extends EsIndex>({
  bookmakers,
  content,
  label,
  resourcesUrl,
  resultIndex,
  styles,
  table,
  tableIndex,
}: ResultTableProps<TIndex>) {
  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
    isOpen: !table.isWalled || tableIndex === 0,
  });

  // Extract main content logic for cleaner JSX
  const mainContent = table.main?.render({
    content,
    resourcesUrl,
    resultIndex,
  });

  const shouldRenderMainContent = table.main && mainContent;

  return (
    <>
      <GridTable.Head
        row={table.headings.row}
        {...(table.shouldToggle
          ? {
              ...headingProps,
              className: triggerClass,
              role: 'rowgroup',
            }
          : {})}
      >
        <GridTable.Row className={styles.tableHeading}>
          {/* @ts-ignore */}
          {table.headings.content.map((heading, index) => (
            <GridTable.Column
              className={heading.className ? styles[heading.className] : ''}
              highlight={heading.highlight}
              isMain={heading.isMain}
              justifyContent={heading.justifyContent}
              key={`${label}-heading-${index}`}
              mobileText={heading.renderMobile?.({ content, resultIndex })}
              mobileView={heading.mobileView}
              tooltipText={heading.tooltipText}
            >
              {heading.shouldShowIndicator ? (
                <>
                  {heading.render({ content, resultIndex })}
                  <Indicator />
                </>
              ) : (
                <>{heading.render({ content, resultIndex })}</>
              )}
            </GridTable.Column>
          ))}
        </GridTable.Row>
      </GridTable.Head>
      {shouldRenderMainContent && (
        <GridTable.Body>
          <GridTable.Row>
            <GridTable.Column alignItems={table.main.alignItems} style={{ gridColumn: '1 / -1' }}>
              {mainContent}
            </GridTable.Column>
          </GridTable.Row>
        </GridTable.Body>
      )}
      {table.body.render({
        bookmakers,
        content,
        label,
        resourcesUrl,
        resultIndex,
        sectionProps,
        styles,
        table,
      })}
      {(!!table.footer && table.footer.render({ content, resourcesUrl, resultIndex })) ?? null}
    </>
  );
}
